import React, { useState, useRef, useEffect } from 'react';
import ChatbotService from '../../desktop/components/ChatbotService';
import ButtonGenerationUtility from '../../desktop/components/ButtonGenerationUtility';
import { FaPaperPlane, FaTrashAlt, FaRobot, FaChevronDown } from 'react-icons/fa';
import '../styles/mobile-chatbot.css';

const MobileChatbotPopup = ({
  isOpen,
  onClose = () => {},
  formData,
  onDataUpdate,
  onProposeUpdate,
  triggerErrorPopup,
  user,
  navigate,
  credits,
  isGated,
  onShowCreditGate,
  onEditClick,
}) => {
  const [messages, setMessages] = useState([{ text: "How can I help you?", sender: "bot" }]);
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [viewportOffsetTop, setViewportOffsetTop] = useState(0);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [loadingState, setLoadingState] = useState({});
  const [isInputFocused, setIsInputFocused] = useState(false);
  const popupRef = useRef(null);

  const handleHandleTap = () => {
    if (popupRef.current) {
      popupRef.current.style.transition = 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
      popupRef.current.style.transform = 'translateY(100%)';
      setTimeout(() => {
        onClose();
        if (popupRef.current) {
          popupRef.current.style.transform = '';
          popupRef.current.style.transition = '';
        }
      }, 300);
    }
  };

  const suggestionChips = [
    { label: "✨ Critique Profile", prompt: "Could you review my current resume sections and give me a detailed critique on what to improve?" },
    { label: "💼 Match Job", prompt: "How can I tailor my summary and experience to better match a target job description?" },
    { label: "🛠️ Check Skills", prompt: "Can you analyze my skills list and suggest any critical tech stack gaps or improvements?" }
  ];

  const initialHeightRef = useRef(window.innerHeight);

  // Note: Body scroll lock is handled by MobileApp.js for the 'coach' tab.
  // No duplicate scroll lock needed here.

  // Toggle keyboard-open class on body
  useEffect(() => {
    if (keyboardOpen) {
      document.body.classList.add('keyboard-open');
    } else {
      document.body.classList.remove('keyboard-open');
    }
    return () => {
      document.body.classList.remove('keyboard-open');
    };
  }, [keyboardOpen]);

  // Detect keyboard open/close and track visual viewport height
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const height = window.visualViewport.height;
        const offsetTop = window.visualViewport.offsetTop;
        setViewportHeight(height);
        setViewportOffsetTop(offsetTop);

        // Update initial height if we see a height that is clearly keyboard-closed
        // and larger than our saved initial height (handles orientation changes or UI collapses)
        if (height > initialHeightRef.current) {
          initialHeightRef.current = height;
        }

        // Keyboard is open if visual viewport height is significantly less than initial height
        const isKeyboardOpen = height < initialHeightRef.current - 150;
        setKeyboardOpen(isKeyboardOpen);
      } else {
        setViewportHeight(window.innerHeight);
        setKeyboardOpen(false);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    handleResize();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  const scrollToLatestMessage = (smooth = true) => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const scrollOptions = smooth ? { behavior: 'smooth' } : {};
      messagesEndRef.current.scrollIntoView(scrollOptions);
    }
  };

  // Scroll to latest message on open (no auto-focus to prevent keyboard intrusion)
  useEffect(() => {
    if (isOpen) {
      scrollToLatestMessage(false);
    }
  }, [isOpen]);

  // Set default message if empty
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ text: "How can I help you?", sender: "bot" }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to latest message and trigger animation when messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToLatestMessage(true);

      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        const timer = setTimeout(() => {
          const lastBubble = messagesContainerRef.current?.querySelector('.message-bubble-wrapper:last-child .message');
          if (lastBubble) {
            lastBubble.classList.add('animate');
          }
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [messages]);

  // Scroll to latest message when keyboard toggles or input focuses
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        scrollToLatestMessage(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [keyboardOpen, isInputFocused, isOpen]);

  // Handle replaying a pending prompt saved before redirecting to login
  useEffect(() => {
    if (isOpen && user && credits) {
      const pendingStr = sessionStorage.getItem('coach_pending');
      if (pendingStr) {
        try {
          const pending = JSON.parse(pendingStr);
          const age = Date.now() - pending.timestamp;
          if (age < 600000 && pending.returnTab === 'coach') {
            sessionStorage.removeItem('coach_pending');
            if (isGated && (credits?.remainingCredits || 0) <= 0) {
              setMessages(prev => [...prev, { text: pending.prompt, sender: "user" }]);
              onShowCreditGate('ai_insufficient');
            } else {
               const runPendingPrompt = async () => {
                setIsProcessing(true);
                try {
                  const botResponse = await ChatbotService.generateResponse(pending.prompt, formData);
                  const buttons = await ButtonGenerationUtility.generateButtons(botResponse);
                  setMessages(prev => [...prev, { text: pending.prompt, sender: "user" }, { text: botResponse.message, sender: "bot", buttons }]);
                } catch (error) {
                  console.error("Error replaying pending prompt:", error);
                  setMessages(prev => [...prev, { text: pending.prompt, sender: "user" }, { text: "Sorry, I couldn't process your request. Please try again later.", sender: "bot" }]);
                } finally {
                  setIsProcessing(false);
                }
              };
              runPendingPrompt();
            }
          } else {
            sessionStorage.removeItem('coach_pending');
          }
        } catch (e) {
          console.error("Error processing pending prompt", e);
          sessionStorage.removeItem('coach_pending');
        }
      }
    }
  }, [isOpen, user, credits, isGated, formData, onShowCreditGate]);

  const handleSendMessage = async () => {
    if (!input.trim() || isProcessing) {
      if (!input.trim()) {
        triggerErrorPopup("Please type a message.");
      }
      return;
    }

    const currentInput = input;

    if (!user) {
      sessionStorage.setItem('coach_pending', JSON.stringify({
        prompt: currentInput,
        returnTab: 'coach',
        timestamp: Date.now()
      }));
      const newMessages = [...messages, { text: currentInput, sender: "user" }];
      setMessages(newMessages);
      setInput("");
      setIsProcessing(true);
      setTimeout(() => {
        setMessages(prev => [...prev, { text: "Please sign in or register to use the AI Coach. I'll remember your question and run it as soon as you sign in!", sender: "bot" }]);
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/sign-in');
        }, 1500);
      }, 500);
      return;
    }

    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      onShowCreditGate('ai_insufficient');
      return;
    }

    const newMessages = [...messages, { text: currentInput, sender: "user" }];
    setMessages(newMessages);
    setInput("");

    if (inputRef.current) {
      inputRef.current.focus();
    }

    setIsProcessing(true);

    try {
      const botResponse = await ChatbotService.generateResponse(currentInput, formData);
      const buttons = await ButtonGenerationUtility.generateButtons(botResponse);

      setMessages([...newMessages, { text: botResponse.message, sender: "bot", buttons }]);
    } catch (error) {
      console.error("Error interacting with Gemini:", error);
      if (error.response?.status === 401) {
        sessionStorage.setItem('coach_pending', JSON.stringify({
          prompt: currentInput,
          returnTab: 'coach',
          timestamp: Date.now()
        }));
        setMessages([
          ...newMessages,
          { text: "Your session has expired. Redirecting to sign in...", sender: "bot" }
        ]);
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/sign-in');
        }, 1500);
      } else {
        setMessages([
          ...newMessages,
          { text: "Sorry, I couldn't process your request. Please try again later.", sender: "bot" },
        ]);
      }
    } finally {
      setIsProcessing(false);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handleKeyPress = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isProcessing) {
        handleSendMessage();
      }
    }
  };

  const handleSuggestionClick = async (prompt) => {
    if (isProcessing) return;

    if (!user) {
      sessionStorage.setItem('coach_pending', JSON.stringify({
        prompt: prompt,
        returnTab: 'coach',
        timestamp: Date.now()
      }));
      const newMessages = [...messages, { text: prompt, sender: "user" }];
      setMessages(newMessages);
      setIsProcessing(true);
      setTimeout(() => {
        setMessages(prev => [...prev, { text: "Please sign in or register to use the AI Coach. I'll remember your question and run it as soon as you sign in!", sender: "bot" }]);
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/sign-in');
        }, 1500);
      }, 500);
      return;
    }

    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      onShowCreditGate('ai_insufficient');
      return;
    }

    const newMessages = [...messages, { text: prompt, sender: "user" }];
    setMessages(newMessages);
    setIsProcessing(true);

    try {
      const botResponse = await ChatbotService.generateResponse(prompt, formData);
      const buttons = await ButtonGenerationUtility.generateButtons(botResponse);

      setMessages([...newMessages, { text: botResponse.message, sender: "bot", buttons }]);
    } catch (error) {
      console.error("Error interacting with Gemini:", error);
      if (error.response?.status === 401) {
        sessionStorage.setItem('coach_pending', JSON.stringify({
          prompt: prompt,
          returnTab: 'coach',
          timestamp: Date.now()
        }));
        setMessages([
          ...newMessages,
          { text: "Your session has expired. Redirecting to sign in...", sender: "bot" }
        ]);
        setTimeout(() => {
          setIsProcessing(false);
          navigate('/sign-in');
        }, 1500);
      } else {
        setMessages([
          ...newMessages,
          { text: "Sorry, I couldn't process your request. Please try again later.", sender: "bot" },
        ]);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearChat = () => {
    if (window.confirm("Are you sure you want to clear this conversation?")) {
      setMessages([{ text: "How can I help you?", sender: "bot" }]);
    }
  };



  if (!isOpen) return null;

  const popupStyle = keyboardOpen ? {
    top: `${viewportOffsetTop}px`,
    height: `${viewportHeight}px`,
    maxHeight: `${viewportHeight}px`,
    bottom: 'auto',
  } : {};

  return (
    <>
      <div className={`chatbot-backdrop ${isOpen ? 'open' : ''}`} onClick={onClose}></div>
      <div 
        className={`chatbot-popup ${isOpen ? 'open' : 'closed'} ${keyboardOpen ? 'keyboard-open' : ''}`} 
        ref={popupRef}
        style={popupStyle}
      >
        <div className="chatbot-header-wrapper">
          <div className="chatbot-drag-handle" onClick={handleHandleTap}></div>
          <div className="chatbot-header">
            <div className="header-center">
              <div className="coach-avatar">
                <FaRobot />
              </div>
              <div className="coach-info">
                <span className="coach-title">AI Career Coach</span>
                <span className="coach-status">● Online</span>
              </div>
            </div>
            <div className="header-right">
              <button className="clear-chat-button" onClick={handleClearChat} aria-label="Clear chat history">
                <FaTrashAlt />
              </button>
              <button className="close-button" onClick={handleHandleTap} aria-label="Close chatbot popup">
                <FaChevronDown />
              </button>
            </div>
          </div>
        </div>

        <div className="chatbot-messages" ref={messagesContainerRef}>
          {messages.length <= 1 ? (
            <div className="chatbot-welcome-panel">
              <div className="welcome-avatar" style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: '28px',
                marginBottom: '16px',
                boxShadow: '0 8px 24px rgba(124, 58, 237, 0.25)'
              }}>
                <FaRobot />
              </div>
              <p className="welcome-description">
                How can I help you improve your resume today? Tap a suggestion or type your question below:
              </p>
              <div className="welcome-suggestions-scroll">
                {suggestionChips.map((chip, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="welcome-suggestion-card"
                    onClick={() => handleSuggestionClick(chip.prompt)}
                    disabled={isProcessing}
                  >
                    <span className="card-label">{chip.label}</span>
                    <span className="card-desc">Tap to ask this</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={`${message.sender}-${index}`} className={`message-bubble-wrapper ${message.sender}`}>
                {message.sender === 'bot' && (
                  <div className="message-avatar">
                    <FaRobot />
                  </div>
                )}
                <div className={`message ${message.sender}`}>
                  <span>{message.text}</span>
                  {message.sender === 'bot' && message.buttons && message.buttons.length > 0 && (
                    <div className="chatbot-action-buttons-tray" style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: '10px' }}>
                      {message.buttons.map((btn, btnIdx) => (
                        <button
                          key={btnIdx}
                          className="chatbot-action-apply-btn"
                          disabled={loadingState[btn.label]}
                          onClick={async () => {
                            if (onProposeUpdate) {
                              onProposeUpdate({
                                sectionKey: btn.section,
                                sectionName: btn.label.replace('Apply to ', ''),
                                proposedContent: btn.content
                              });
                            } else {
                              setLoadingState(prevState => ({
                                ...prevState,
                                [btn.label]: true,
                              }));

                              const updatedData = await btn.action((isLoading) => {
                                setLoadingState(prevState => ({
                                  ...prevState,
                                  [btn.label]: isLoading,
                                }));
                              });

                              if (updatedData && onDataUpdate) {
                                onDataUpdate(updatedData);
                              }

                              setLoadingState(prevState => ({
                                ...prevState,
                                [btn.label]: false,
                              }));
                            }
                          }}
                        >
                          {loadingState[btn.label] ? (
                            <div className="spinner"></div>
                          ) : (
                            btn.label
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {isProcessing && (
            <div className="message-bubble-wrapper bot">
              <div className="message-avatar">
                <FaRobot />
              </div>
              <div className="message bot processing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef}></div>
        </div>

        {messages.length > 1 && !isInputFocused && !keyboardOpen && (
          <div className="chatbot-suggestions-container">
            <div className="suggestions-scroll">
              {suggestionChips.map((chip, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(chip.prompt)}
                  disabled={isProcessing}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="chatbot-input-container">
          <div className="input-field-wrapper">
            <input
              type="text"
              value={input}
              ref={inputRef}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onKeyPress={handleKeyPress}
              placeholder="Ask the AI Coach..."
              disabled={isProcessing}
            />
            <button className="send-message-button" onClick={handleSendMessage} disabled={isProcessing} aria-label="Send prompt">
              <FaPaperPlane />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileChatbotPopup;